import { ExpandedState } from "@tanstack/react-table";
import camelCase from "lodash/camelCase";
import { NotificationSetting } from "PFTypes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { NotificationRow } from "./notifications_settings_form.types";
import { buildNotificationRows } from "./notifications_settings_form.utils";
import { useNotificationCategoryLabel } from "./use_notification_category_label";

export const useNotificationRows = (
  notificationsSettings: NotificationSetting[],
  term: string | undefined
) => {
  const { t } = useTranslation("notificationsSettings");
  const getCategoryLabel = useNotificationCategoryLabel();
  const lowerTerm = term?.toLowerCase() ?? "";

  const allRows = useMemo(() => buildNotificationRows(notificationsSettings), [notificationsSettings]);

  const filteredRows = useMemo((): NotificationRow[] => {
    if (!lowerTerm) {
      return allRows;
    }

    return allRows
      .map((row): NotificationRow | null => {
        if (row.group === null) {
          const label = getCategoryLabel(row.setting!).toLowerCase();
          return label.includes(lowerTerm) ? row : null;
        }

        const groupLabel = (
          t(`group.${camelCase(row.group)}` as unknown as TemplateStringsArray) as string
        ).toLowerCase();

        if (groupLabel.includes(lowerTerm)) {
          return row;
        }

        const filteredChildren = row.childrenTableData.filter((child) =>
          getCategoryLabel(child.setting!).toLowerCase().includes(lowerTerm)
        );

        return filteredChildren.length > 0 ? { ...row, childrenTableData: filteredChildren } : null;
      })
      .filter((row) => row !== null);
  }, [allRows, lowerTerm, t, getCategoryLabel]);

  const [expandedState, setExpandedState] = useState<ExpandedState>(() =>
    allRows.reduce<Record<string, boolean>>((acc, row) => {
      if (row.group) {
        acc[row.rowId] = true;
      }

      return acc;
    }, {})
  );

  const foundCount = filteredRows.reduce(
    (acc, row) => acc + (row.group ? row.childrenTableData.length : 1),
    0
  );

  return { allRows, filteredRows, foundCount, expandedState, setExpandedState };
};
